"use client";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Copy,
  Check,
  Lock,
  Zap,
  AlertCircle,
  Wallet,
  CreditCard,
  Send,
  ListChecks,
  ChevronRight,
  Bot,
  Download,
  ExternalLink,
  Link2,
  List,
  ArrowUp,
  Globe,
  Braces,
  KeyRound,
} from "lucide-react";

// ────────────────────────────────────────────────────────────────────────────
// Public documentation for the CashTap Checkout API. Living reference for
// merchants: authentication, every endpoint, request/response shapes, error
// codes, code samples in curl + JavaScript, webhooks, and settlement.
// Sticky table of contents on the left so long-form scanning works.
// ────────────────────────────────────────────────────────────────────────────

type SectionDef = {
  id: string;
  label: string;
  icon: typeof BookOpen;
  // Surfaces a green "new" pill in both navs so a returning reader sees
  // what changed since they last read the docs.
  isNew?: boolean;
};

const SECTIONS: SectionDef[] = [
  { id: "overview",         label: "Overview",                 icon: BookOpen },
  { id: "auth",             label: "Authentication",           icon: Lock },
  { id: "errors",           label: "Error handling",           icon: AlertCircle },
  { id: "create-session",   label: "Create Checkout Session",  icon: CreditCard },
  { id: "retrieve-session", label: "Retrieve Session Status",  icon: ListChecks },
  { id: "payment-methods",  label: "Payment methods",          icon: Wallet },
  { id: "webhooks",         label: "Webhooks",                 icon: Send, isNew: true },
  { id: "settlement",       label: "Settlement & fees",        icon: Zap },
  // Keep `llms` LAST: the scroll-spy below special-cases the final id.
  { id: "llms",             label: "For AI assistants",        icon: Bot },
];

// Scroll-spy: tracks which section is currently in view so the table of
// contents can highlight the reader's location. Uses IntersectionObserver
// with a top-weighted rootMargin so a section becomes "active" as its
// heading nears the top of the viewport (not when it's centered).
//
// Bottom-of-page edge case: the IntersectionObserver's rootMargin
// (`-96px 0px -65% 0px`) requires a section's heading to be in the top
// 35% of the viewport to register. The LAST section can't satisfy this
// — there's no content below it to push it up — so clicking the last
// sidebar link "did nothing visually" (page scrolled, but the active
// highlight never moved). We patch that by also listening to window
// scroll and force-activating the last section once the reader is
// within a small threshold of the document bottom.
function useActiveSection(ids: string[]): string {
  const [active, setActive] = useState<string>(ids[0] ?? "");

  useEffect(() => {
    if (ids.length === 0) return;
    const lastId = ids[ids.length - 1];
    const visible = new Map<string, number>();

    // Single source of truth: recompute active from BOTH the
    // IntersectionObserver's view (`visible`) and the document scroll
    // position. The trailing-edge / bottom-of-page case (last section's
    // heading lands BELOW the IO observation zone) wins unconditionally
    // because otherwise the IO callback would fire moments later and
    // overwrite us with the second-to-last section.
    const computeActive = () => {
      const viewport = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      const scrollBottom = window.scrollY + viewport;

      // Bottom-of-page detection. Two signals (either is sufficient):
      //   • Within 64px of the document bottom — covers short last
      //     sections that can never push their heading high enough.
      //   • The last section's top has crossed the 60%-from-top line
      //     — covers the case where the section IS pretty long and
      //     the user has scrolled a meaningful amount into it.
      const lastEl = document.getElementById(lastId);
      const lastTop = lastEl?.getBoundingClientRect().top ?? Infinity;
      const nearDocBottom = scrollBottom >= docHeight - 64;
      const scrolledIntoLast = lastTop < viewport * 0.6;
      if (nearDocBottom || scrolledIntoLast) {
        setActive((cur) => (cur === lastId ? cur : lastId));
        return;
      }

      // Normal case: pick the topmost section currently intersecting
      // the IO observation zone.
      let top: { id: string; y: number } | null = null;
      for (const id of visible.keys()) {
        const el = document.getElementById(id);
        if (!el) continue;
        const y = el.getBoundingClientRect().top;
        if (!top || y < top.y) top = { id, y };
      }
      if (top) {
        const winner = top.id;
        setActive((cur) => (cur === winner ? cur : winner));
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) visible.set(e.target.id, e.intersectionRatio);
          else visible.delete(e.target.id);
        }
        computeActive();
      },
      { rootMargin: "-96px 0px -65% 0px", threshold: [0, 0.25, 1] },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    // Scroll listener also routes through `computeActive` so the
    // bottom-of-page branch keeps re-asserting itself while the reader
    // continues scrolling at the trailing edge.
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        computeActive();
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Fire once on mount so deep-link (`/developers/docs#llms`) lands
    // on the right active state without needing the reader to scroll.
    computeActive();

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [ids]);

  return active;
}

export default function DocumentationPage() {
  const active = useActiveSection(SECTIONS.map((s) => s.id));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  // Keep the active sidebar item visible. The IntersectionObserver in
  // useActiveSection already shifts the `active` highlight as the reader
  // scrolls the content; this effect also pulls the matching <a> into
  // view inside whichever nav surface is currently rendered (sticky
  // desktop sidebar or mobile drawer), so a long sidebar that overflows
  // the viewport doesn't strand the highlighted item off-screen.
  useEffect(() => {
    if (!active) return;
    // We use a small `requestAnimationFrame` so the scroll happens after
    // React has applied the new active class — otherwise the item we're
    // scrolling to might be the previous one.
    const raf = requestAnimationFrame(() => {
      // Two selectors: desktop sidebar nav vs mobile drawer nav. Both
      // contain anchors with href={`#${id}`}; the first matching link
      // wins (they don't both render at the same breakpoint).
      const link = document.querySelector<HTMLAnchorElement>(
        `aside nav a[href="#${active}"], .lg\\:hidden nav a[href="#${active}"]`,
      );
      if (link) {
        link.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [active]);

  // Smooth-scroll to a hash anchor on initial mount (so /developers/docs#auth
  // works when shared via link).
  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, []);

  // Reveal the "back to top" affordance once the reader has scrolled past
  // the fold. Throttled via rAF to avoid layout thrash on scroll.
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setShowTop(window.scrollY > 640);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const activeLabel = SECTIONS.find((s) => s.id === active)?.label ?? "Contents";

  return (
    // Top-level dark shell — previously the page inherited its
    // background from <Layout>, but now that this route is public and
    // lives outside Layout, the shell has to be self-contained.
    // Gradient + text color mirror the legal pages (PolicyShell) so
    // every public surface shares one visual identity. `print:` resets
    // keep documentation legible if someone prints / PDFs the page.
    <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] text-white print:bg-white print:text-black">
      <div className="max-w-6xl mx-auto py-8 sm:py-10 px-4">
      <PageHeader />

      {/* Mobile section navigator — the desktop sidebar is hidden on small
          screens, so a collapsible "On this page" menu keeps every section
          reachable without a long scroll. Sticky under the app header. */}
      <div className="lg:hidden sticky top-16 z-20 -mx-4 px-4 mb-6">
        <button
          onClick={() => setMobileNavOpen((o) => !o)}
          aria-expanded={mobileNavOpen}
          className="w-full flex items-center gap-2.5 px-4 py-3 rounded-xl border border-gray-800 bg-[#0F1419]/95 backdrop-blur text-left shadow-lg shadow-black/40"
        >
          <List className="w-4 h-4 text-[#61CE70] shrink-0" />
          <span className="text-[10px] uppercase tracking-[0.16em] text-gray-500 font-semibold">On this page</span>
          <span className="text-sm text-white font-medium truncate flex-1">{activeLabel}</span>
          <ChevronRight
            className={`w-4 h-4 text-gray-500 shrink-0 transition-transform duration-200 ${mobileNavOpen ? "rotate-90" : ""}`}
          />
        </button>
        <div
          className="overflow-hidden transition-[max-height] duration-300 ease-out"
          style={{ maxHeight: mobileNavOpen ? `${SECTIONS.length * 44 + 16}px` : "0px" }}
        >
          <nav className="mt-2 p-2 rounded-xl border border-gray-800 bg-[#0F1419]/95 backdrop-blur space-y-0.5">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === active;
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  onClick={() => setMobileNavOpen(false)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive ? "bg-[#61CE70]/10 text-white" : "text-gray-400"
                  }`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-[#61CE70]" : "text-[#61CE70]/60"}`} />
                  <span className="min-w-0 flex-1">{s.label}</span>
                  {s.isNew && (
                    <span className="text-[9px] uppercase tracking-wider text-[#61CE70] border border-[#61CE70]/30 bg-[#61CE70]/10 px-1.5 py-0.5 rounded-full shrink-0">
                      new
                    </span>
                  )}
                </a>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden lg:block">
          {/* Container is explicitly scrollable + height-capped so the
              `scrollIntoView` call in the active-section effect uses
              this element as the nearest scrollable ancestor — not the
              page. On tall viewports the sidebar fits without scrolling
              and the call is a no-op; on shorter viewports the sidebar
              scrolls internally to keep the active item visible. */}
          <div className="sticky top-20 rounded-2xl border border-gray-800/80 bg-[#0F1419]/60 p-3 overflow-y-auto max-h-[calc(100vh-6rem)]">
            <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-semibold mb-3 px-2.5">
              Contents
            </p>
            <nav className="space-y-0.5">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const isActive = s.id === active;
                return (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    aria-current={isActive ? "location" : undefined}
                    className={`group relative flex items-center gap-2.5 pl-3.5 pr-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                      isActive
                        ? "bg-[#61CE70]/10 text-white font-medium"
                        : "text-gray-400 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    {/* Active indicator bar — communicates location by more
                        than color alone (color-not-only). */}
                    <span
                      className={`absolute left-0 top-1/2 -translate-y-1/2 w-0.5 rounded-full transition-all duration-200 ${
                        isActive ? "h-5 bg-[#61CE70]" : "h-0 bg-transparent"
                      }`}
                    />
                    <Icon
                      className={`w-3.5 h-3.5 shrink-0 transition-colors ${
                        isActive ? "text-[#61CE70]" : "text-[#61CE70]/60 group-hover:text-[#61CE70]/80"
                      }`}
                    />
                    <span className="min-w-0 flex-1 truncate">{s.label}</span>
                    {s.isNew && (
                      <span className="text-[9px] uppercase tracking-wider text-[#61CE70] border border-[#61CE70]/30 bg-[#61CE70]/10 px-1.5 py-0.5 rounded-full shrink-0 leading-none">
                        new
                      </span>
                    )}
                  </a>
                );
              })}
            </nav>
          </div>
        </aside>

        <article className="min-w-0 max-w-3xl">
          <Overview />
          <Authentication />
          <ErrorHandling />
          <CreateSession />
          <RetrieveSession />
          <PaymentMethods />
          <Webhooks />
          <Settlement />
          <Llms />
        </article>
      </div>

      {/* Back to top — appears after scrolling past the fold. */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
        className={`fixed bottom-6 right-6 z-30 w-11 h-11 rounded-full bg-[#61CE70] text-black shadow-lg shadow-[#61CE70]/20 flex items-center justify-center transition-all duration-200 hover:bg-[#50B060] ${
          showTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
        }`}
      >
        <ArrowUp className="w-5 h-5" strokeWidth={2.5} />
      </button>
      </div>
    </div>
  );
}

function PageHeader() {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#61CE70]/20 via-[#50B060]/10 to-transparent border border-[#61CE70]/30 flex items-center justify-center">
          <BookOpen className="w-5 h-5 text-[#61CE70]" />
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#61CE70] font-semibold">CashTap Developers</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Documentation</h1>
        </div>
      </div>
      <p className="mt-3 text-gray-400 text-sm max-w-2xl">
        Reference for the CashTap Checkout API. Read this to integrate hosted checkout into your site, mobile app, or back-end service.
      </p>
    </div>
  );
}

// ─── Sections ──────────────────────────────────────────────────────────────

function Overview() {
  return (
    <Section id="overview" icon={<BookOpen className="w-4 h-4" />} title="Overview">
      <p>
        The CashTap Checkout API lets your application accept payments in <Pill>USD</Pill> from your customers through a hosted, branded checkout page at <Code>pay.cashtap.cash</Code>. You stay on your domain. Your customer is redirected to CashTap to complete the payment, then returned to a URL you specify.
      </p>
      <p>
        It's similar in shape to Stripe Checkout. Your backend creates a <strong>Checkout Session</strong>, gets back a hosted URL, and redirects the buyer there. CashTap handles payment collection, fee deduction, and on-chain settlement.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-[1.7fr_1fr_1fr] gap-2.5 my-5">
        <BaseUrlStat url="https://api.cashtap.cash/checkout/v1" />
        <Stat icon={<KeyRound className="w-3.5 h-3.5" />} label="Auth" value="Bearer key" />
        <Stat icon={<Braces className="w-3.5 h-3.5" />} label="Format" value="JSON" />
      </div>
      <Callout tone="info">
        <strong>Live keys only.</strong> CashTap doesn't ship a separate test mode. Use small amounts during integration. Refunds for failed integration attempts can be handled manually through support.
      </Callout>
    </Section>
  );
}

function Authentication() {
  return (
    <Section id="auth" icon={<Lock className="w-4 h-4" />} title="Authentication">
      <p>
        Authenticate by sending your secret key as a Bearer token in the <Code>Authorization</Code> header. Keys are issued from your <strong>Developers → API Settings</strong> tab and look like <Code>sk_live_…</Code>.
      </p>
      <CodeBlock language="bash" code={`curl https://api.cashtap.cash/checkout/v1/sessions \\
  -H "Authorization: Bearer sk_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ ... }'`} />
      <Callout tone="warn">
        <strong>Never expose your secret key in client-side code.</strong> Calls to the Checkout API must originate from your backend. If a key leaks, revoke it from the dashboard — revocation is instant.
      </Callout>
      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Key rotation</h4>
      <p>
        You can mint up to multiple active keys at the same time. Best practice for rotation: create a new key, deploy it, verify traffic, then revoke the old one. Both will work simultaneously during the cut-over.
      </p>
    </Section>
  );
}

function ErrorHandling() {
  return (
    <Section id="errors" icon={<AlertCircle className="w-4 h-4" />} title="Error handling">
      <p>
        Every error response uses the same envelope so you can rely on a consistent shape:
      </p>
      <CodeBlock language="json" code={`{
  "error": {
    "code": "invalid_request",
    "message": "amount must be at least 0.50 USD.",
    "request_id": "req_2026053027abc"
  }
}`} />
      <p>
        Always include <Code>request_id</Code> when contacting support. HTTP status mapping:
      </p>
      <div className="rounded-xl border border-gray-800 overflow-hidden mt-2">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[460px] text-sm">
          <thead className="bg-gray-900/60 text-gray-400 text-xs">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-left px-3 py-2 font-semibold">Code</th>
              <th className="text-left px-3 py-2 font-semibold">Meaning</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/70 text-gray-300">
            <ErrRow status="400" code="invalid_request" meaning="Missing or invalid parameters (includes an unknown payment method id)" />
            <ErrRow status="400" code="payment_method_not_enabled" meaning="A requested payment method is valid but not enabled on your account" />
            <ErrRow status="401" code="authentication_required" meaning="Missing or malformed Authorization header" />
            <ErrRow status="403" code="permission_denied" meaning="Key revoked or redirect domain not on allowlist" />
            <ErrRow status="404" code="not_found" meaning="No session / merchant with that id" />
            <ErrRow status="422" code="unprocessable_entity" meaning="Semantically invalid (e.g. amount below minimum)" />
            <ErrRow status="429" code="rate_limited" meaning="Too many requests — back off and retry" />
            <ErrRow status="503" code="provider_unavailable" meaning="A downstream payment provider is degraded" />
            <ErrRow status="500" code="api_error" meaning="Unexpected server error — include request_id" />
          </tbody>
        </table>
        </div>
      </div>
    </Section>
  );
}

function CreateSession() {
  return (
    <Section id="create-session" icon={<CreditCard className="w-4 h-4" />} title="Create Checkout Session">
      <Endpoint method="POST" path="/checkout/v1/sessions" />
      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Request body</h4>
      <CodeBlock language="json" code={`{
  "amount": 50.00,
  "line_items": [
    { "name": "Premium Plan", "description": "Monthly subscription", "quantity": 1, "unit_amount": 50.00 }
  ],
  "customer_email": "buyer@acme.com",
  "payment_methods": ["wallet", "cashapp"],
  "success_url": "https://acme.com/thanks?order=12345",
  "cancel_url": "https://acme.com/cart",
  "metadata": { "order_id": "12345" }
}`} />
      <ParamTable
        rows={[
          ["amount", "number", "Required. Total amount in USD. Min 0.50, max 100,000. Individual payment methods have narrower limits — see Payment methods."],
          ["line_items", "array", "Optional. Displayed to the buyer on the checkout page. Each item: { name, description?, quantity, unit_amount }."],
          ["customer_email", "string", "Optional. Pre-fills the email on the checkout page."],
          ["payment_methods", "array", "Optional. Subset of the methods enabled on your account: \"coinbase\" (Card, Bank or Apple Pay, $5–$10,000), \"wallet\" (Wallet Transfer, $0.50–$100,000), \"cashapp\" (CashApp Pay, $1 minimum). Requesting a method you have not enabled returns 400 payment_method_not_enabled; an unknown id returns 400 invalid_request. \"card-bank\" and \"lightning-btc\" are deprecated aliases for \"coinbase\" and \"cashapp\". Omit to offer everything you have enabled."],
          ["success_url", "string", "Required. Where to send the buyer on successful payment. Domain must be on your allowlist."],
          ["cancel_url", "string", "Required. Where to send the buyer on cancel or expiration."],
          ["metadata", "object", "Optional. Up to 20 string key/value pairs. Echoed back in webhooks and the retrieve endpoint."],
        ]}
      />
      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Response</h4>
      <CodeBlock language="json" code={`{
  "id": "cs_live_abc123…",
  "url": "https://pay.cashtap.cash/c/cs_live_abc123…",
  "status": "pending",
  "amount": 50.00,
  "payment_methods": ["wallet", "cashapp"],
  "expires_at": 1717104000,
  "created_at": 1717102200
}`} />
      <p>
        The 201 body is the full session shape — the same fields as <a href="#retrieve-session">Retrieve Session Status</a>. <Code>payment_methods</Code> is the canonical list the buyer will actually be offered, so assert on it right after creating a session.
      </p>
      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Example — JavaScript</h4>
      <CodeBlock language="javascript" code={`const res = await fetch("https://api.cashtap.cash/checkout/v1/sessions", {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${process.env.CASHTAP_SECRET_KEY}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    amount: 50.0,
    line_items: [{ name: "Premium Plan", quantity: 1, unit_amount: 50.0 }],
    payment_methods: ["wallet", "cashapp"],
    success_url: "https://acme.com/thanks?order=12345",
    cancel_url: "https://acme.com/cart",
    metadata: { order_id: "12345" },
  }),
});

const session = await res.json();
// Redirect buyer to session.url
res.redirect(303, session.url);`} />
    </Section>
  );
}

function RetrieveSession() {
  return (
    <Section id="retrieve-session" icon={<ListChecks className="w-4 h-4" />} title="Retrieve Session Status">
      <Endpoint method="GET" path="/checkout/v1/sessions/{session_id}" />
      <p>
        Poll this endpoint, or rely on webhooks, to learn when a session
        is paid. The response always echoes the input fields you sent on
        creation (<Code>amount</Code>, <Code>metadata</Code>,
        <Code>customer_email</Code>) and adds settlement details once the
        buyer pays.
      </p>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Path parameters</h4>
      <ParamTable
        rows={[
          ["session_id", "string", "Required. The id returned from Create Checkout Session (e.g. cs_live_abc123)."],
        ]}
      />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Example — curl</h4>
      <CodeBlock language="bash" code={`curl https://api.cashtap.cash/checkout/v1/sessions/cs_live_abc123 \\
  -H "Authorization: Bearer sk_live_YOUR_KEY"`} />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Example — JavaScript</h4>
      <CodeBlock language="javascript" code={`async function getCheckoutSession(sessionId) {
  const res = await fetch(
    \`https://api.cashtap.cash/checkout/v1/sessions/\${sessionId}\`,
    {
      headers: {
        Authorization: \`Bearer \${process.env.CASHTAP_SECRET_KEY}\`,
      },
    },
  );
  if (!res.ok) {
    const err = await res.json();
    throw new Error(\`Retrieve failed: \${err.error?.message}\`);
  }
  return res.json();
}

const session = await getCheckoutSession("cs_live_abc123");
if (session.status === "completed") {
  // Mark the order as paid in your DB. session.net_amount is what
  // landed in your wallet (post-fee).
}`} />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Response fields</h4>
      <ParamTable
        rows={[
          ["id",                 "string",  "The session id, same as the path parameter."],
          ["url",                "string",  "Hosted checkout page for this session. Redirect the buyer here."],
          ["status",             "string",  "One of: pending, processing, completed, expired, failed."],
          ["amount",             "number",  "Total amount in USD you requested on creation."],
          ["amount_received",    "number",  "What the buyer's payment actually delivered, before the platform fee. Can be slightly below amount (bridge and on-ramp fees). null until completed."],
          ["fee_amount",         "number",  "Platform fee deducted before settlement. null until completed."],
          ["net_amount",         "number",  "Amount that lands in your wallet. null until completed."],
          ["line_items",         "array",   "The line items you sent on creation. Empty array if none."],
          ["payment_method",     "string",  "Which method the buyer used. null until they pick one."],
          ["payment_methods",    "array",   "Canonical payment method ids offered on this session: what you requested, intersected with the methods enabled on your account. Deprecated aliases come back normalised (card-bank as coinbase, lightning-btc as cashapp)."],
          ["customer_email",     "string",  "Email the buyer entered (or the one you pre-filled)."],
          ["success_url",        "string",  "The success redirect you sent on creation."],
          ["cancel_url",         "string",  "The cancel redirect you sent on creation."],
          ["metadata",           "object",  "Whatever metadata you sent on creation, echoed back verbatim."],
          ["paid_at",            "number",  "Unix timestamp of when the buyer's payment was confirmed. null until completed."],
          ["incoming_tx_hash",   "string",  "On-chain hash of the buyer's deposit. null until completed."],
          ["settlement_tx_hash", "string",  "On-chain hash of the seller-leg sweep to your wallet. null until completed."],
          ["fee_sweep_tx_hash",  "string",  "On-chain hash of the platform-fee sweep. null until completed."],
          ["expires_at",         "number",  "Unix timestamp at which the session will be auto-marked expired."],
          ["created_at",         "number",  "Unix timestamp at which the session was created."],
        ]}
      />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Response — <code className="text-amber-300">pending</code></h4>
      <p>While the buyer is still on the hosted checkout page, the settlement fields are null:</p>
      <CodeBlock language="json" code={`{
  "id": "cs_live_abc123…",
  "status": "pending",
  "amount": 50.00,
  "fee_amount": null,
  "net_amount": null,
  "payment_method": null,
  "customer_email": "buyer@acme.com",
  "paid_at": null,
  "incoming_tx_hash": null,
  "settlement_tx_hash": null,
  "metadata": { "order_id": "12345" },
  "expires_at": 1717104000,
  "created_at": 1717102200
}`} />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Response — <code className="text-blue-300">processing</code></h4>
      <p>The buyer's deposit has landed on-chain but the settlement leg hasn't confirmed yet (typically &lt; 30 seconds):</p>
      <CodeBlock language="json" code={`{
  "id": "cs_live_abc123…",
  "status": "processing",
  "amount": 50.00,
  "fee_amount": 1.75,
  "net_amount": 48.25,
  "payment_method": "wallet",
  "customer_email": "buyer@acme.com",
  "paid_at": 1717102560,
  "incoming_tx_hash": "0xabc…",
  "settlement_tx_hash": null,
  "metadata": { "order_id": "12345" }
}`} />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Response — <code className="text-[#61CE70]">completed</code></h4>
      <p>Everything has settled. Check <Code>amount_received</Code> against <Code>amount</Code>, then fulfill the order.</p>
      <CodeBlock language="json" code={`{
  "id": "cs_live_abc123…",
  "status": "completed",
  "amount": 50.00,
  "amount_received": 50.00,
  "fee_amount": 1.75,
  "net_amount": 48.25,
  "payment_method": "wallet",
  "customer_email": "buyer@acme.com",
  "paid_at": 1717102560,
  "incoming_tx_hash": "0xabc…",
  "settlement_tx_hash": "0xdef…",
  "metadata": { "order_id": "12345" }
}`} />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Response — <code className="text-red-300">expired</code> / <code className="text-red-300">failed</code></h4>
      <p>The session timed out (60 minutes) without a confirmed payment, or a payment arrived but settlement failed on our side. Do not fulfill the order.</p>
      <CodeBlock language="json" code={`{
  "id": "cs_live_abc123…",
  "status": "expired",
  "amount": 50.00,
  "fee_amount": null,
  "net_amount": null,
  "payment_method": null,
  "customer_email": "buyer@acme.com",
  "paid_at": null,
  "incoming_tx_hash": null,
  "settlement_tx_hash": null,
  "metadata": { "order_id": "12345" }
}`} />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Polling guidance</h4>
      <Callout tone="info">
        <strong>Prefer webhooks.</strong> If you must poll, do it at
        most once every <Code>5s</Code> while the session is
        <Code>pending</Code> or <Code>processing</Code>, and stop at a
        terminal state (<Code>completed</Code>, <Code>expired</Code>,
        <Code>failed</Code>). Aggressive polling will trip the rate
        limiter.
      </Callout>

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Session lifecycle</h4>
      <Lifecycle />
    </Section>
  );
}

function PaymentMethods() {
  return (
    <Section id="payment-methods" icon={<Wallet className="w-4 h-4" />} title="Payment methods">
      <p>
        Three methods can be shown to a buyer, and each one is a toggle under <strong>API Settings → Allowed payment methods</strong>. The <strong>Method</strong> column below is the exact dashboard label, so the id you send and the switch you flipped are unmistakably the same thing.
      </p>
      <div className="rounded-xl border border-gray-800 overflow-hidden mt-3">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] text-sm">
          <thead className="bg-gray-900/60 text-gray-400 text-xs">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">ID</th>
              <th className="text-left px-3 py-2 font-semibold">Method (dashboard label)</th>
              <th className="text-left px-3 py-2 font-semibold">What the buyer does</th>
              <th className="text-left px-3 py-2 font-semibold">Min</th>
              <th className="text-left px-3 py-2 font-semibold">Max</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/70 text-gray-300">
            <MethodRow id="coinbase" label="Card, Bank or Apple Pay" use="Pays with a debit card, a bank account or Apple Pay — powered by Coinbase Onramp." min="$5.00" max="$10,000" />
            <MethodRow id="wallet" label="Wallet Transfer (USDC)" use="Sends USDC on Base from any wallet" min="$0.50" max="$100,000" />
            <MethodRow id="cashapp" label="CashApp Pay" use="US buyers, instant — routed over Lightning under the hood" min="$1.00" max="≈ $16,000 *" />
          </tbody>
        </table>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        * CashApp Pay is routed over Lightning and the swap provider caps a single
        exchange at 0.2 BTC, so its ceiling moves with the BTC price rather than
        sitting at a round dollar figure. Treat anything above $10,000 as
        best-effort and offer Wallet Transfer alongside it.
      </p>

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Amount limits</h4>
      <p>
        Two separate checks apply, at two different moments.
      </p>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-300">
        <li>
          <strong>The session amount</strong> must be between <Code>0.50</Code> and{" "}
          <Code>100000</Code>. This is checked when you call{" "}
          <Code>POST /checkout/v1/sessions</Code> and fails immediately with{" "}
          <Code>400</Code> <Code>invalid_request</Code>.
        </li>
        <li>
          <strong>Each method's own range</strong> is checked later — when the buyer
          picks that method on the hosted page. A session for an amount outside a
          method's range is still created successfully; the buyer simply cannot
          finish with that method.
        </li>
      </ul>
      <Callout tone="warn">
        <strong>Always leave the buyer a way to pay.</strong> Below <Code>$5</Code> the
        hosted checkout marks Card/Bank/Apple Pay unavailable and points the buyer at
        Wallet Transfer or CashApp Pay; above <Code>$10,000</Code> Coinbase rejects the
        payment outright. Below <Code>$1</Code>, CashApp Pay cannot be started either.
        On amounts near either edge, offer <Code>wallet</Code> as well — it is the only
        method that covers the full <Code>$0.50</Code>–<Code>$100,000</Code> range.
      </Callout>
      <p>
        <strong>Tolerance.</strong> Underpayments within 5% of the session amount
        (floor: 1 cent) still settle as paid — that absorbs Lightning slippage and
        on-ramp rounding. Overpayments settle too: you receive whatever actually
        arrived, minus the fee. Either way,{" "}
        <Code>amount_received</Code> is the number to check before you fulfill.
      </p>

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Which methods a session offers</h4>
      <p>
        The methods on a session are the intersection of two lists: <strong>what you request</strong> in <Code>payment_methods</Code> and <strong>what is enabled on your account</strong>. Omit <Code>payment_methods</Code> and the session offers everything you have enabled. A session can never enable a method you have not pre-authorised in the dashboard.
      </p>
      <Callout tone="warn">
        <strong>Behaviour change.</strong> A valid method that was not enabled on your account used to be dropped <em>silently</em> — the session came back with the remaining methods, no error and no explanation. It now fails with <Code>400</Code> <Code>payment_method_not_enabled</Code>, and the message names both the rejected ids and the ids you do have enabled. Requests that only ask for enabled methods behave exactly as before.
      </Callout>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-300">
        <li>Valid id, not enabled on your account → <Code>400</Code> <Code>payment_method_not_enabled</Code>. Enable it under <strong>API Settings → Allowed payment methods</strong>, or drop it from the request.</li>
        <li>Not a payment method id at all → <Code>400</Code> <Code>invalid_request</Code>, with the supported ids in the message.</li>
        <li>Every method switched off on your account → <Code>400</Code> <Code>payment_method_not_enabled</Code> even when you omit <Code>payment_methods</Code>, because the checkout page would have no payment buttons on it.</li>
      </ul>
      <CodeBlock language="json" code={`{
  "error": {
    "code": "payment_method_not_enabled",
    "message": "Payment method \\"coinbase\\" is not enabled on your account. Enabled: cashapp, wallet. Enable it under API Settings → Allowed payment methods.",
    "request_id": "req_2026092612abc"
  }
}`} />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Deprecated aliases</h4>
      <p>
        <Code>card-bank</Code> and <Code>lightning-btc</Code> are still accepted, but they are aliases kept for compatibility rather than methods of their own: <Code>card-bank</Code> normalises to <Code>coinbase</Code>, <Code>lightning-btc</Code> normalises to <Code>cashapp</Code>. Normalisation happens before the intersection above and before anything is stored, so requesting <Code>["cashapp", "card-bank"]</Code> returns a session whose <Code>payment_methods</Code> is <Code>["cashapp", "coinbase"]</Code> — the canonical ids, which is also what the buyer is shown. Send canonical ids in new code.
      </p>
      <Callout tone="info">
        <strong><Code>peer</Code> is not currently offered to buyers.</strong> The id stays accepted for accounts that already have Peer Extension enabled, but the hosted checkout does not render it, so never make it a session's only method.
      </Callout>
    </Section>
  );
}

function Webhooks() {
  return (
    <Section id="webhooks" icon={<Send className="w-4 h-4" />} title="Webhooks" isNew>
      <p>
        CashTap sends a signed HTTPS <Code>POST</Code> to your server when a checkout session reaches a final state. Add up to 5 endpoints under <strong>Developers → Webhooks</strong>, each with its own signing secret and event subscriptions.
      </p>
      <Callout tone="info">
        <strong>Webhooks replace polling.</strong> Return any 2xx within 10 seconds and do slow work in the background.
      </Callout>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Quick start</h4>
      <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-300">
        <li>Add an endpoint under <strong>Developers → Webhooks</strong>: an <Code>https://</Code> URL and the events it should receive.</li>
        <li>Save the <Code>whsec_…</Code> signing secret. It is shown once, so store it in an environment variable.</li>
        <li>Verify the <Code>X-CashTap-Signature</Code> header against the raw request body.</li>
        <li>Return a 2xx right away, then process the event.</li>
        <li>Send a test event from the dashboard and check the result in the delivery history.</li>
      </ol>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Event types</h4>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-300">
        <li>
          <Code>checkout.session.completed</Code> — Payment received and settled. Compare <Code>data.object.amount_received</Code> with <Code>amount</Code> before fulfilling: bridge and on-ramp fees can deliver slightly less (normally within 5%), and support may settle a larger underpayment after contacting you.
        </li>
        <li><Code>checkout.session.expired</Code> — The session timed out without a confirmed payment.</li>
        <li><Code>checkout.session.failed</Code> — A payment arrived but settlement failed on our side. CashTap support resolves these; do not fulfill.</li>
      </ul>
      <Callout tone="warn">
        <Code>expired</Code> or <Code>failed</Code> can later be followed by <Code>completed</Code> for the same session (late payment recovery). <Code>completed</Code> is final.
      </Callout>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Payload</h4>
      <p>Every event uses the same JSON envelope:</p>
      <CodeBlock language="json" code={`{
  "id": "evt_FjhHMSLvI1sX4xUDxI4Wbw5h",
  "object": "event",
  "type": "checkout.session.completed",
  "api_version": "2026-09-01",
  "created": 1788998042,
  "livemode": true,
  "data": {
    "object": {
      "object": "checkout.session",
      "id": "cs_live_AAAAAAAAAAAAAAAAAAAAAA",
      "url": "https://pay.cashtap.cash/c/cs_live_AAAAAAAAAAAAAAAAAAAAAA",
      "status": "completed",
      "amount": 25,
      "amount_received": 24.91,
      "fee_amount": 0.75,
      "net_amount": 24.16,
      "line_items": [
        { "name": "Pro plan", "description": null, "image_url": null, "quantity": 1, "unit_amount": 25 }
      ],
      "payment_method": "cashapp",
      "payment_methods": ["coinbase", "wallet", "cashapp"],
      "customer_email": "jane@example.com",
      "success_url": "https://acme-store.com/thanks",
      "cancel_url": "https://acme-store.com/cart",
      "metadata": { "order_id": "A-1042" },
      "paid_at": 1788997990,
      "incoming_tx_hash": null,
      "settlement_tx_hash": "0x9f…c1",
      "fee_sweep_tx_hash": "0x1a…7e",
      "expires_at": 1789001590,
      "created_at": 1788997990
    }
  }
}`} />
      <ParamTable
        rows={[
          ["id",          "string",  "Unique event id (evt_…). The same id is sent on every retry, on every resend and to every endpoint. Deduplicate on it."],
          ["object",      "string",  "Always \"event\"."],
          ["type",        "string",  "One of the three event types above."],
          ["api_version", "string",  "Version of the payload format. Currently 2026-09-01."],
          ["created",     "number",  "Unix timestamp at which the event was created."],
          ["livemode",    "boolean", "true for real sessions. false for test events sent from the dashboard."],
          ["data.object", "object",  "The checkout session as it was when the event happened. status always matches the event type."],
        ]}
      />
      <p className="mt-3">
        <Code>data.object</Code> matches <Code>GET /checkout/v1/sessions/{`{session_id}`}</Code> (plus <Code>"object": "checkout.session"</Code>); <Code>metadata</Code> is echoed. In <Code>expired</Code> and <Code>failed</Code> events the settlement fields (<Code>amount_received</Code>, <Code>fee_amount</Code>, <Code>net_amount</Code>, <Code>settlement_tx_hash</Code>, <Code>fee_sweep_tx_hash</Code>) are <Code>null</Code>; <Code>expired</Code> also has <Code>payment_method</Code>, <Code>paid_at</Code> and <Code>incoming_tx_hash</Code> set to <Code>null</Code>.
      </p>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Headers</h4>
      <div className="rounded-xl border border-gray-800 overflow-hidden mt-2">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-gray-900/60 text-gray-400 text-xs">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Header</th>
              <th className="text-left px-3 py-2 font-semibold">Value</th>
              <th className="text-left px-3 py-2 font-semibold">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/70 text-gray-300">
            <HeaderRow name="Content-Type" value="application/json; charset=utf-8" notes="Always JSON, UTF-8." />
            <HeaderRow name="User-Agent" value="CashTap-Webhooks/1.0 (+https://cashtap.cash/developers/docs#webhooks)" notes="Fixed." />
            <HeaderRow name="X-CashTap-Signature" value="t=<unix seconds>,v1=<hex>" notes="Fresh t on every attempt. Two v1 values while a rotated secret overlaps (current secret first)." />
            <HeaderRow name="X-CashTap-Event-Id" value="evt_…" notes="Same across retries, resends and endpoints. Equals the body id." />
            <HeaderRow name="X-CashTap-Event-Type" value="checkout.session.completed" notes="Equals the body type." />
            <HeaderRow name="X-CashTap-Delivery-Id" value="whd_…" notes="One per delivery. A resend is a new delivery id — dedupe on the event id." />
            <HeaderRow name="X-CashTap-Attempt" value="1 to 6" notes="Attempt number of an automatic delivery. Always 1 for test events and resends." />
          </tbody>
        </table>
        </div>
      </div>
      <p className="mt-3">
        CashTap never sends cookies, an <Code>Authorization</Code> header or custom headers.
      </p>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Signature verification</h4>
      <p>
        Every request carries <Code>X-CashTap-Signature: t=&lt;unix seconds&gt;,v1=&lt;hex&gt;</Code>. Verify it before you parse or trust the body:
      </p>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-300">
        <li>The signed string is <Code>&lt;t&gt;.&lt;raw body&gt;</Code>: the timestamp, a dot, then the exact bytes of the request body.</li>
        <li><Code>v1</Code> is the hex HMAC-SHA256 of that string. The key is the whole <Code>whsec_…</Code> string as UTF-8. <strong>Do not base64-decode it</strong> (this differs from Svix).</li>
        <li>Reject timestamps older than 5 minutes to block replayed requests.</li>
        <li>The header can carry more than one <Code>v1</Code>. Accept the request if any of them matches, using a constant-time comparison.</li>
      </ul>

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Example — Node.js (Express)</h4>
      <CodeBlock language="javascript" code={`const crypto = require("crypto");
// Register BEFORE express.json():
// app.post("/webhooks/cashtap", express.raw({ type: "application/json" }), handler)
function verifyCashTapSignature(rawBody, header, secret, toleranceSec) {
  if (toleranceSec === undefined) toleranceSec = 300;
  if (typeof header !== "string" || header.length > 1024) return false;
  let t = null;
  const sigs = [];
  for (const part of header.split(",")) {
    const i = part.indexOf("=");
    if (i === -1) continue;
    const k = part.slice(0, i).trim();
    const v = part.slice(i + 1).trim();
    if (k === "t") t = v;
    else if (k === "v1") sigs.push(v);
  }
  if (!/^\\d{1,12}$/.test(t || "")) return false;
  if (Math.abs(Date.now() / 1000 - Number(t)) > toleranceSec) return false;   // replay window
  const expected = crypto.createHmac("sha256", secret).update(t + ".").update(rawBody).digest();
  return sigs.slice(0, 5).some(function (s) {
    return /^[0-9a-f]{64}$/i.test(s) && crypto.timingSafeEqual(Buffer.from(s, "hex"), expected);
  });
}
function handler(req, res) {
  const ok = verifyCashTapSignature(req.body, req.get("X-CashTap-Signature"), process.env.CASHTAP_WEBHOOK_SECRET);
  if (!ok) return res.sendStatus(400);
  const event = JSON.parse(req.body.toString("utf8"));
  // 1) dedupe on event.id (unique index)  2) respond 2xx fast  3) process asynchronously
  if (event.type === "checkout.session.completed") {
    const s = event.data.object;
    if (s.amount_received < s.amount) { /* underpaid within tolerance: decide before fulfilling */ }
  }
  res.sendStatus(200);
}`} />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Example — Python</h4>
      <CodeBlock language="python" code={`import hmac, hashlib, time
def verify_cashtap_signature(raw_body: bytes, header: str, secret: str, tolerance: float = 300) -> bool:
    t, sigs = None, []
    for part in (header or "")[:1024].split(","):
        key, sep, value = part.strip().partition("=")
        if not sep:
            continue
        if key == "t":
            t = value
        elif key == "v1":
            sigs.append(value)
    if not t or not t.isascii() or not t.isdigit() or len(t) > 12:
        return False
    if abs(time.time() - int(t)) > tolerance:
        return False
    expected = hmac.new(secret.encode(), t.encode() + b"." + raw_body, hashlib.sha256).hexdigest().encode()
    return any(hmac.compare_digest(expected, s.strip().lower().encode()) for s in sigs[:5])
# Flask: raw_body = request.get_data(); header = request.headers.get("X-CashTap-Signature", "")`} />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Example — Next.js (App Router)</h4>
      <CodeBlock language="javascript" code={`// Next.js App Router: app/api/webhooks/cashtap/route.js
// verifyCashTapSignature is the function from the Node.js example above.
export async function POST(req) {
  const raw = await req.text();                      // raw body, before any JSON parsing
  const ok = verifyCashTapSignature(raw, req.headers.get("x-cashtap-signature"), process.env.CASHTAP_WEBHOOK_SECRET);
  if (!ok) return new Response(null, { status: 400 });
  const event = JSON.parse(raw);
  return new Response(null, { status: 200 });
}`} />

      <h4 className="text-white font-semibold mt-6 mb-2 text-sm">Test your verifier</h4>
      <p>Your code should accept this request and reject it if you change any character of the body:</p>
      <CodeBlock language="text" code={`secret : whsec_test_secret_do_not_use
t      : 1767225600
body   : {"id":"evt_123","type":"checkout.session.completed"}
v1     : 66a710e3cc2913be941031d9e7691c085570a75feb121fa1cc59070421dd44a2
header : t=1767225600,v1=66a710e3cc2913be941031d9e7691c085570a75feb121fa1cc59070421dd44a2`} />
      <p>
        The vector's timestamp is fixed (2026-01-01): call your verifier with the tolerance disabled, or freeze the clock.
      </p>
      <Callout tone="warn">
        <strong>Signature mismatch checklist</strong>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Hash the raw request bytes, not re-serialized JSON.</li>
          <li>Register the webhook route BEFORE any global JSON body parser. An <Code>app.use(express.json())</Code> registered earlier has already consumed the body.</li>
          <li>Use THIS endpoint's secret. Each endpoint has its own.</li>
          <li>Use the whole <Code>whsec_…</Code> string as the key, not a decoded version of it.</li>
          <li>Keep your server clock synced (NTP), or the timestamp check fails.</li>
          <li>During a secret rotation, accept the request if any <Code>v1</Code> matches.</li>
        </ul>
      </Callout>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Responding</h4>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-300">
        <li>Any 2xx status marks the delivery successful. Everything else is a failed attempt.</li>
        <li>Respond within 10 seconds, or the attempt times out.</li>
        <li>Redirects are never followed. A 3xx is a failed attempt.</li>
        <li>The first 2,000 characters of your response are stored for debugging and shown in the delivery history.</li>
        <li>Endpoints must use <Code>https://</Code> on port 443 or 8443. Private and internal addresses are blocked, so use a tunnel to test against your local machine.</li>
      </ul>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Retries</h4>
      <div className="rounded-xl border border-gray-800 overflow-hidden mt-2">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[460px] text-sm">
          <thead className="bg-gray-900/60 text-gray-400 text-xs">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Attempt</th>
              <th className="text-left px-3 py-2 font-semibold">Wait after the previous attempt</th>
              <th className="text-left px-3 py-2 font-semibold">Time since the event</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/70 text-gray-300">
            <RetryRow attempt="1" wait="Immediately" elapsed="—" />
            <RetryRow attempt="2" wait="≈ 1 min" elapsed="≈ 1 min" />
            <RetryRow attempt="3" wait="≈ 5 min" elapsed="≈ 6 min" />
            <RetryRow attempt="4" wait="≈ 30 min" elapsed="≈ 36 min" />
            <RetryRow attempt="5" wait="≈ 2 h" elapsed="≈ 2 h 36 min" />
            <RetryRow attempt="6" wait="≈ 9 h" elapsed="≈ 11 h 36 min" />
          </tbody>
        </table>
        </div>
      </div>
      <p className="mt-3">
        6 attempts over about 12 hours, ±10% jitter. <Code>Retry-After</Code> on 429/503 is honored up to 1 hour. After the sixth failed attempt the delivery is marked failed. Test events and resends are a single attempt.
      </p>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Failure alerts and auto-disable</h4>
      <p>
        We email you on the first failure of a streak, when a delivery exhausts its retries, and when the endpoint recovers. You can turn these alerts off per endpoint.
      </p>
      <p>
        An endpoint is disabled after 3 days (72 hours) of continuous failure. We email you when that happens, even if alerts are turned off for the endpoint. Events for a paused or disabled endpoint are recorded as skipped. Re-enable the endpoint from the dashboard. You can resend skipped events from the delivery history.
      </p>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Rotating secrets</h4>
      <p>
        Rotating a secret shows you the new <Code>whsec_…</Code> once. The previous secret stays valid for 24 hours, and during that overlap every request carries two <Code>v1</Code> values (new secret first), so you can deploy the new secret without dropping events. Choose <strong>revoke immediately</strong> if the old secret leaked. Only one previous secret is kept: rotating again ends the earlier overlap.
      </p>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Best practices</h4>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-300">
        <li>Dedupe on <Code>event.id</Code>. Delivery is at-least-once, and resends reuse the event id.</li>
        <li>Don't rely on ordering. Events can arrive out of order.</li>
        <li>Verify the signature before parsing the body.</li>
        <li>Only the body is authenticated. Treat the <Code>X-CashTap-*</Code> headers as hints.</li>
        <li>Acknowledge fast, process asynchronously.</li>
        <li>Keep one secret per endpoint, in an environment variable.</li>
        <li>For high-value fulfillment, confirm with <Code>GET /checkout/v1/sessions/{`{session_id}`}</Code> before shipping.</li>
        <li>Check <Code>livemode</Code> to ignore test events. Test events reference a <Code>cs_test_</Code> session that cannot be retrieved.</li>
        <li>Use <Code>event.id</Code> for idempotency on your side; the API does not accept idempotency keys yet.</li>
      </ul>

      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Testing from the dashboard</h4>
      <p>
        <strong>Send test</strong> posts a signed sample event (<Code>livemode: false</Code>) to an endpoint and shows the live result: status code, response time and the start of your response body. Every delivery is kept in the delivery history for 30 days. From there you can <strong>Retry now</strong> a delivery that is waiting for its next attempt, or <strong>Resend</strong> one that was delivered, failed or skipped.
      </p>
      <Link
        to="/developers/webhooks"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#61CE70] hover:bg-[#50B060] text-black text-xs font-semibold transition-colors"
      >
        <Send className="w-3.5 h-3.5" />
        Open Webhooks
      </Link>
    </Section>
  );
}

function Settlement() {
  return (
    <Section id="settlement" icon={<Zap className="w-4 h-4" />} title="Settlement & fees">
      <p>
        When a buyer pays, funds first land in a CashTap-controlled dedicated wallet. We then:
      </p>
      <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-300 mt-2">
        <li>Sweep the platform fee to CashTap's admin wallet.</li>
        <li>Sweep the net amount to your CashTap wallet.</li>
        <li>Mark the session <Code>completed</Code> and fire the <Code>checkout.session.completed</Code> webhook.</li>
      </ol>
      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">Default fee</h4>
      <div className="rounded-xl border border-[#61CE70]/20 bg-[#61CE70]/5 p-4">
        <p className="text-white font-mono text-lg">2.9% + $0.30 per successful checkout</p>
        <p className="text-gray-400 text-xs mt-1">Negotiable for higher volume — contact support to discuss custom pricing.</p>
      </div>
      <p className="mt-4 text-sm text-gray-300">
        The fee is automatically deducted before settlement — you receive <Code>net_amount</Code> in your wallet. The full breakdown is available in the retrieve session response.
      </p>
    </Section>
  );
}

function Llms() {
  return (
    <Section id="llms" icon={<Bot className="w-4 h-4" />} title="For AI assistants">
      <p>
        We publish an <Code>llms.txt</Code> file so AI coding assistants (Claude, ChatGPT, Copilot, Cursor, etc.) can ingest the Checkout API spec without you having to copy-paste docs. Point your agent at this URL and it can write integration code directly against the latest reference.
      </p>
      <div className="my-4 rounded-2xl border border-[#61CE70]/25 bg-[#61CE70]/5 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm flex items-center gap-2">
              <Bot className="w-4 h-4 text-[#61CE70]" />
              CashTap Checkout API llms.txt
            </p>
            <p className="text-gray-400 text-xs mt-0.5 font-mono break-all">
              https://cashtap.cash/llms.txt
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href="/llms.txt"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900/60 hover:bg-gray-800 border border-gray-800 text-gray-200 text-xs font-medium transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View
            </a>
            <a
              href="/llms.txt"
              download="cashtap-llms.txt"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#61CE70] hover:bg-[#50B060] text-black text-xs font-semibold transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </a>
          </div>
        </div>
      </div>
      <h4 className="text-white font-semibold mt-5 mb-2 text-sm">How to use it</h4>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-300">
        <li>
          <strong>Claude / Claude Code:</strong> paste the URL into your conversation, or add it to your project's context.
        </li>
        <li>
          <strong>Cursor:</strong> add it as a documentation source under Settings → Indexing & Docs.
        </li>
        <li>
          <strong>ChatGPT (with browsing):</strong> tell it to fetch the URL before writing integration code.
        </li>
        <li>
          <strong>GitHub Copilot:</strong> add the file to your repo so Copilot can index it.
        </li>
      </ul>
      <Callout tone="info">
        <strong>What's inside:</strong> a structured Markdown summary of every endpoint, parameter, response shape, error code, payment-method id, and webhook event, plus minimal code samples. It's regenerated whenever the public spec changes.
      </Callout>
    </Section>
  );
}

// ─── Helper components ─────────────────────────────────────────────────────

function Section({
  id,
  icon,
  title,
  isNew,
  children,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  // Marks a section that shipped recently — a green pill beside the
  // heading, matching the one in the contents nav.
  isNew?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-12 first:mt-0 scroll-mt-24">
      {/* group/anchor lets the deep-link affordance reveal on hover (desktop)
          while staying keyboard-focusable for everyone else. */}
      <div className="group/anchor flex items-center gap-3 mb-5 flex-wrap">
        <div className="w-9 h-9 rounded-lg bg-[#61CE70]/10 border border-[#61CE70]/20 flex items-center justify-center text-[#61CE70] shrink-0">
          {icon}
        </div>
        <h2 className="text-xl font-bold text-white tracking-tight">{title}</h2>
        {isNew && (
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#61CE70] px-2 py-0.5 rounded-full border border-[#61CE70]/30 bg-[#61CE70]/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#61CE70]" />
            New
          </span>
        )}
        <a
          href={`#${id}`}
          aria-label={`Link to ${title}`}
          className="ml-0.5 p-1 rounded-md text-gray-600 hover:text-[#61CE70] hover:bg-[#61CE70]/10 opacity-0 group-hover/anchor:opacity-100 focus-visible:opacity-100 transition-opacity"
        >
          <Link2 className="w-4 h-4" />
        </a>
      </div>
      <div className="prose prose-invert max-w-none [&_p]:text-gray-300 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-3 [&_strong]:text-white">
        {children}
      </div>
    </section>
  );
}

function Endpoint({ method, path }: { method: string; path: string }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-gray-800 bg-gray-900/40 px-3 py-2.5 my-2 font-mono text-sm">
      <span
        className={`px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wider ${
          method === "GET"
            ? "bg-blue-500/15 text-blue-300 border border-blue-500/25"
            : "bg-[#61CE70]/15 text-[#61CE70] border border-[#61CE70]/25"
        }`}
      >
        {method}
      </span>
      <code className="text-gray-200 break-all">{path}</code>
    </div>
  );
}

function CodeBlock({ language, code }: { language: string; code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="my-3 rounded-xl bg-[#0a0d12] border border-gray-800 overflow-hidden">
      {/* Header bar — language label + always-visible copy button. A
          hover-only copy button is invisible and unusable on touch
          (hover-vs-tap), so it stays visible at all times here. */}
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-800/80 bg-white/[0.02]">
        <span className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-semibold">
          {language}
        </span>
        <button
          onClick={() => {
            navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="inline-flex items-center gap-1.5 px-2 py-1 -mr-1 rounded-md text-[11px] font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          title="Copy code"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-[#61CE70]" />
              <span className="text-[#61CE70]">Copied</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-[13px] text-gray-200 font-mono leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function ParamTable({ rows }: { rows: Array<[string, string, string]> }) {
  return (
    <div className="rounded-xl border border-gray-800 overflow-hidden mt-2">
      <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-sm">
        <thead className="bg-gray-900/60 text-gray-400 text-xs">
          <tr>
            <th className="text-left px-3 py-2 font-semibold">Field</th>
            <th className="text-left px-3 py-2 font-semibold">Type</th>
            <th className="text-left px-3 py-2 font-semibold">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800/70">
          {rows.map(([name, type, desc]) => (
            <tr key={name}>
              <td className="px-3 py-2 font-mono text-[#61CE70] text-xs whitespace-nowrap align-top">{name}</td>
              <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap align-top">{type}</td>
              <td className="px-3 py-2 text-gray-300 text-sm leading-relaxed">{desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function ErrRow({ status, code, meaning }: { status: string; code: string; meaning: string }) {
  return (
    <tr>
      <td className="px-3 py-2 text-gray-400 font-mono text-xs whitespace-nowrap">{status}</td>
      <td className="px-3 py-2 text-[#61CE70] font-mono text-xs whitespace-nowrap">{code}</td>
      <td className="px-3 py-2 text-sm">{meaning}</td>
    </tr>
  );
}

function MethodRow({
  id,
  label,
  use,
  min,
  max,
}: {
  id: string;
  label: string;
  use: string;
  // Per-method amount range, checked when the buyer picks the method
  // on the hosted page — not at session creation. Kept in the same
  // table as the ids so an integrator sizing a payment sees both at once.
  min: string;
  max: string;
}) {
  return (
    <tr>
      <td className="px-3 py-2 text-[#61CE70] font-mono text-xs whitespace-nowrap">{id}</td>
      <td className="px-3 py-2 text-white text-sm font-medium whitespace-nowrap">{label}</td>
      <td className="px-3 py-2 text-gray-400 text-sm">{use}</td>
      <td className="px-3 py-2 text-gray-200 text-sm font-mono text-xs whitespace-nowrap">{min}</td>
      <td className="px-3 py-2 text-gray-200 text-sm font-mono text-xs whitespace-nowrap">{max}</td>
    </tr>
  );
}

// Webhook delivery headers. The value column is mono and allowed to wrap:
// the User-Agent string is long enough to blow out the table otherwise.
function HeaderRow({ name, value, notes }: { name: string; value: string; notes: string }) {
  return (
    <tr>
      <td className="px-3 py-2 text-[#61CE70] font-mono text-xs whitespace-nowrap align-top">{name}</td>
      <td className="px-3 py-2 text-gray-400 font-mono text-xs break-all align-top">{value}</td>
      <td className="px-3 py-2 text-sm">{notes}</td>
    </tr>
  );
}

function RetryRow({ attempt, wait, elapsed }: { attempt: string; wait: string; elapsed: string }) {
  return (
    <tr>
      <td className="px-3 py-2 text-[#61CE70] font-mono text-xs whitespace-nowrap">{attempt}</td>
      <td className="px-3 py-2 text-sm whitespace-nowrap">{wait}</td>
      <td className="px-3 py-2 text-gray-400 text-sm whitespace-nowrap">{elapsed}</td>
    </tr>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block px-2 py-0.5 rounded-md text-xs font-mono bg-[#61CE70]/10 text-[#61CE70] border border-[#61CE70]/20 mx-1">
      {children}
    </span>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return <code className="px-1.5 py-0.5 rounded-md bg-gray-800/60 text-[#61CE70] font-mono text-[12px]">{children}</code>;
}

// Compact "quick fact" tile — icon + label + a single mono value. Used for
// the at-a-glance API facts (Auth, Format) under the overview.
function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-3 flex flex-col">
      <div className="flex items-center gap-1.5 text-gray-500">
        <span className="text-[#61CE70]/70">{icon}</span>
        <p className="text-[10px] uppercase tracking-[0.16em] font-semibold">{label}</p>
      </div>
      <p className="text-white font-mono text-sm mt-1.5 leading-tight">{value}</p>
    </div>
  );
}

// Base URL is the one fact developers actually copy, so it gets a wider
// tile with a one-tap copy button and a focus ring on the whole card.
function BaseUrlStat({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="col-span-2 sm:col-span-1 group rounded-xl border border-gray-800 bg-gray-900/40 p-3 hover:border-[#61CE70]/30 transition-colors">
      <div className="flex items-center gap-1.5 text-gray-500">
        <Globe className="w-3.5 h-3.5 text-[#61CE70]/70" />
        <p className="text-[10px] uppercase tracking-[0.16em] font-semibold">Base URL</p>
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <code className="text-white font-mono text-sm leading-tight break-all flex-1 min-w-0">
          {url}
        </code>
        <button
          onClick={() => {
            navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          title="Copy base URL"
          aria-label="Copy base URL"
          className="shrink-0 p-1.5 -my-0.5 rounded-md text-gray-500 hover:text-white hover:bg-white/5 transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-[#61CE70]" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

function Callout({ tone, children }: { tone: "info" | "warn"; children: React.ReactNode }) {
  const t =
    tone === "warn"
      ? "border-amber-500/30 bg-amber-500/5 text-amber-100"
      : "border-[#61CE70]/25 bg-[#61CE70]/5 text-gray-200";
  const Icon = tone === "warn" ? AlertCircle : Lock;
  const iconColor = tone === "warn" ? "text-amber-400" : "text-[#61CE70]";
  return (
    <div className={`my-4 p-4 rounded-xl border ${t} flex items-start gap-3`}>
      <Icon className={`w-4 h-4 ${iconColor} shrink-0 mt-0.5`} />
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function Lifecycle() {
  const steps = [
    { status: "pending", desc: "Session created — waiting for buyer to pay" },
    { status: "processing", desc: "Buyer's payment detected, settling on-chain" },
    { status: "completed", desc: "Funds settled; checkout.session.completed webhook sent" },
    { status: "expired", desc: "60 minutes passed without payment" },
    { status: "failed", desc: "Payment arrived but settlement failed; CashTap support resolves it" },
  ];
  return (
    <div className="rounded-xl border border-gray-800 overflow-hidden mt-2">
      {steps.map((s, i) => (
        <div
          key={s.status}
          className={`flex items-center gap-3 p-3 ${i > 0 ? "border-t border-gray-800/70" : ""}`}
        >
          <span className="font-mono text-xs text-[#61CE70] w-24 shrink-0">{s.status}</span>
          <ChevronRight className="w-3.5 h-3.5 text-gray-600 shrink-0" />
          <span className="text-gray-300 text-sm flex-1">{s.desc}</span>
        </div>
      ))}
    </div>
  );
}
